import React, { useState, useEffect } from "react";
import { Box, Grid, Typography, alpha, useTheme } from "@mui/material";
import POSHeader from "./POSHeader";
import {
  DashboardOffersSection,
  DashboardOrdersSection,
  DashboardTablesSection,
  DashboardQuickActionsSection,
  DashboardStatsCard,
} from "./Dashboard";
import TableServices from "../../../services/TableService";
import OrderServices from "../../../services/OrderService";
import OfferServices from "../../../services/OfferService";
import { toast } from "react-toastify";
import {
  Restaurant,
  CheckCircle,
  HourglassEmpty,
  TableRestaurant,
  EventSeat,
  Kitchen,
  Dining,
} from "@mui/icons-material";
import { useUserWithCompany } from "hooks/useCompany";

// Category Section Component for better organization
const CategorySection = ({ title, subtitle, icon: Icon, stats, iconColor }) => {
  const theme = useTheme();

  return (
    <Box
      sx={{
        bgcolor: "white",
        borderRadius: "16px",
        p: 3,
        border: "1px solid",
        borderColor: alpha(theme.palette.divider, 0.08),
        boxShadow: `0 1px 3px ${alpha("#000", 0.04)}`,
      }}
    >
      {/* Category Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          mb: 3,
        }}
      >
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: "8px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: alpha(iconColor, 0.1),
          }}
        >
          <Icon sx={{ fontSize: 20, color: iconColor }} />
        </Box>
        <Box>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              color: theme.palette.text.primary,
              fontSize: "1rem",
              lineHeight: 1.2,
            }}
          >
            {title}
          </Typography>
          <Typography
            variant="caption"
            sx={{
              color: theme.palette.text.secondary,
              fontSize: "0.75rem",
            }}
          >
            {subtitle}
          </Typography>
        </Box>
      </Box>

      {/* Stats Grid */}
      <Grid container spacing={2}>
        {stats.map((stat, index) => (
          <Grid
            key={index}
            size={{ xs: 12, sm: 6, md: stats.length === 3 ? 4 : 6 }}
          >
            <DashboardStatsCard {...stat} />
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

const POSDashboard = ({
  mainTab,
  setMainTab,
  isFullscreen,
  toggleFullscreen,
  fullscreenLabel,
  handleTablesClick,
  handleOffersClick,
  onCreateDineIn,
  onCreateTakeaway,
  tables,
  onViewAllOrders,
  onViewAllTables,
  onSelectOrder,
  onSelectTable,
  onPOSClick,
  onHomeClick,
  onFilterByStatus,
  orderTypeConfig,
}) => {
  const { companyId, user, userType } = useUserWithCompany();

  // Check if user is a waiter
  const isWaiter = userType === "waiter" || user?.type === "waiter";

  const [loadedTables, setLoadedTables] = useState([]);
  const [tablesData, setTablesData] = useState({
    data: [],
    totalItems: 0,
    totalPages: 0,
    currentPage: 0,
  });
  const [loadedOrders, setLoadedOrders] = useState([]);
  const [ordersData, setOrdersData] = useState({
    data: [],
    totalItems: 0,
    totalPages: 0,
    currentPage: 0,
  });
  const [loadedOffers, setLoadedOffers] = useState([]);
  const [customerMobileSearch, setCustomerMobileSearch] = useState("");
  const [orderTypeFilter, setOrderTypeFilter] = useState("all");
  const [ordersViewMode, setOrdersViewMode] = useState("grid");
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [tablesLoading, setTablesLoading] = useState(false);

  const getOrdersLimitByViewMode = (viewMode = "grid") =>
    viewMode === "list" ? 12 : 8;

  // Fetch tables, orders, and offers on mount
  useEffect(() => {
    fetchTables();
    fetchOrders(0, customerMobileSearch, orderTypeFilter, ordersViewMode);
    fetchOffers();

    // Auto-refresh orders every 30 seconds
    const interval = setInterval(() => {
      fetchOrders(
        ordersData.currentPage || 0,
        customerMobileSearch,
        orderTypeFilter,
        ordersViewMode,
      );
    }, 30000);

    return () => clearInterval(interval);
  }, []);

  // Fetch orders when customerMobileSearch or orderTypeFilter changes
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      fetchOrders(0, customerMobileSearch, orderTypeFilter, ordersViewMode); // Reset to page 0 when search changes
    }, 300); // Debounce for 300ms

    return () => clearTimeout(timeoutId);
  }, [customerMobileSearch, orderTypeFilter, ordersViewMode]);

  useEffect(() => {
    fetchOrders(0, customerMobileSearch, orderTypeFilter, ordersViewMode);
  }, [ordersViewMode]);

  const fetchTables = async (page = 0) => {
    try {
      setTablesLoading(true);
      const response = await TableServices.getAll({
        limit: 4,
        page: page,
        companyId: companyId,
      });

      if (response && response.data?.view) {
        const { data, totalItems, totalPages, currentPage } =
          response.data.view;

        // Update both states for backward compatibility
        setLoadedTables(data);
        setTablesData({
          data: data,
          totalItems: totalItems,
          totalPages: totalPages,
          currentPage: currentPage,
        });
      }
    } catch (err) {
      console.error("Error fetching tables:", err);
      toast.error("Failed to load tables");
    } finally {
      setTablesLoading(false);
    }
  };

  const fetchOrders = async (
    page = 0,
    customerMobile = "",
    orderType = "all",
    viewMode = "grid",
  ) => {
    try {
      setOrdersLoading(true);
      // Fetch ongoing orders (pending, preparing, ready, active)
      const params = {
        limit: getOrdersLimitByViewMode(viewMode),
        page: page,
        companyId: companyId,
        // Remove status filter to get all active orders
      };

      // Add customerMobile parameter if provided
      if (customerMobile.trim()) {
        params.customerMobile = customerMobile.trim();
      }

      // Add orderType parameter if not "all"
      if (orderType && orderType !== "all") {
        params.orderType = orderType;
      }

      const response = await OrderServices.getAllOrders(params);

      console.log("Dashboard orders response:", response);

      if (response && response.data?.view) {
        const { data, totalItems, totalPages, currentPage } =
          response.data.view;

        // Fetch full details for each order to get OrderItems
        const ordersWithDetails = await Promise.all(
          data.map(async (order) => {
            try {
              const detailsResponse = await OrderServices.getOrderById(
                order.id,
                { companyId },
              );
              if (detailsResponse?.data?.view) {
                return detailsResponse.data.view;
              }
              return order; // Fallback to basic order if details fetch fails
            } catch (error) {
              console.error(
                `Error fetching details for order ${order.id}:`,
                error,
              );
              return order; // Fallback to basic order
            }
          }),
        );

        // Update both states for backward compatibility
        setLoadedOrders(ordersWithDetails);
        setOrdersData({
          data: ordersWithDetails,
          totalItems: totalItems,
          totalPages: totalPages,
          currentPage: currentPage,
          pageSize: params.limit,
        });
      }
    } catch (err) {
      console.error("Error fetching orders:", err);
      // Silent fail for dashboard - don't show toast to avoid spam during auto-refresh
    } finally {
      setOrdersLoading(false);
    }
  };

  const fetchOffers = async () => {
    try {
      const response = await OfferServices.getAll({
        isActive: true,
        limit: 5,
        companyId: companyId,
      });

      console.log("Dashboard offers response:", response);

      if (response && response.data?.view?.data) {
        setLoadedOffers(response.data.view.data);
      }
    } catch (err) {
      console.error("Error fetching offers:", err);
      // Silent fail for dashboard
    }
  };

  // Handle orders pagination
  const handleOrdersPageChange = (newPage) => {
    fetchOrders(newPage, customerMobileSearch, orderTypeFilter, ordersViewMode);
  };

  // Handle tables pagination
  // const handleTablesPageChange = (newPage) => {
  //   fetchTables(newPage);
  // };

  // // Use loaded data from API
  // const displayOrders = loadedOrders;
  // const displayTables = loadedTables.length > 0 ? loadedTables : tables || [];

  // Calculate stats from real table data
  // const getTableStatus = (table) => {
  //   const hasActiveOrders = table.Orders && table.Orders.length > 0;
  //   return hasActiveOrders ? "occupied" : "available";
  // };

  // const stats = {
  //   pending: displayOrders.filter((o) => o.status === "pending").length,
  //   confirmed: displayOrders.filter((o) => o.status === "confirmed").length,
  //   preparing: displayOrders.filter((o) => o.status === "preparing").length,
  //   ready: displayOrders.filter((o) => o.status === "ready").length,
  //   served: displayOrders.filter((o) => o.status === "served").length,
  //   availableTables: displayTables.filter(
  //     (t) => getTableStatus(t) === "available",
  //   ).length,
  //   occupiedTables: displayTables.filter(
  //     (t) => getTableStatus(t) === "occupied",
  //   ).length,
  // };

  // const activeOffers = loadedOffers.slice(0, 3);

  // Stats data organized by category for better user understanding
  // const orderStats = [
  //   {
  //     title: "Pending",
  //     value: stats.pending,
  //     icon: HourglassEmpty,
  //     gradient: ["#f59e0b", "#d97706"],
  //     subtitle: "New orders received",
  //     description: "Awaiting confirmation",
  //     status: "pending",
  //     onClick: onFilterByStatus,
  //   },
  //   {
  //     title: "In Kitchen",
  //     value: stats.preparing,
  //     icon: Restaurant,
  //     gradient: ["#667eea", "#764ba2"],
  //     subtitle: "Orders being prepared",
  //     description: "Currently cooking",
  //     status: "preparing",
  //     onClick: onFilterByStatus,
  //   },
  //   {
  //     title: "Ready",
  //     value: stats.ready,
  //     icon: CheckCircle,
  //     gradient: ["#10b981", "#059669"],
  //     subtitle: "Ready for serving",
  //     description: "Food is ready",
  //     status: "ready",
  //     onClick: onFilterByStatus,
  //   },
  // ];

  // const tableStats = [
  //   {
  //     title: "Available",
  //     value: stats.availableTables,
  //     icon: TableRestaurant,
  //     gradient: ["#43e97b", "#38f9d7"],
  //     subtitle: "Ready for guests",
  //     description: "Empty tables",
  //   },
  //   {
  //     title: "Occupied",
  //     value: stats.occupiedTables,
  //     icon: EventSeat,
  //     gradient: ["#f59e0b", "#d97706"],
  //     subtitle: "Currently dining",
  //     description: "Tables in use",
  //   },
  // ];

  return (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        bgcolor: "#f8fafc",
        overflow: "hidden",
      }}
    >
      {/* Header */}
      <POSHeader
        mainTab={mainTab}
        setMainTab={setMainTab}
        isFullscreen={isFullscreen}
        toggleFullscreen={toggleFullscreen}
        fullscreenLabel={fullscreenLabel}
        onTablesClick={handleTablesClick}
        onOffersClick={handleOffersClick}
        onPOSClick={onPOSClick}
        onHomeClick={onHomeClick}
      />

      {/* Dashboard Content */}
      <Box
        sx={{
          flex: 1,
          overflow: "auto",
          p: 3,
          "&::-webkit-scrollbar": {
            width: "8px",
          },
          "&::-webkit-scrollbar-track": {
            bgcolor: "transparent",
          },
          "&::-webkit-scrollbar-thumb": {
            bgcolor: "rgba(0,0,0,0.2)",
            borderRadius: "4px",
            "&:hover": {
              bgcolor: "rgba(0,0,0,0.3)",
            },
          },
        }}
      >
        <Grid container spacing={3}>
          {/* Stats Cards Section with Categories */}
          {/* <Grid size={{ xs: 12 }}>
            <Grid container spacing={3}>
              {/* Order Statistics Section */}
          {/* <Grid size={{ xs: 12, md: 7 }}>
            <CategorySection
              title="Order Status"
              subtitle="Real-time kitchen & service status"
              icon={Kitchen}
              stats={orderStats}
              iconColor="#667eea"
            />
          </Grid> */}

          {/* Table Statistics Section
              <Grid size={{ xs: 12, md: 5 }}>
                <CategorySection
                  title="Table Status"
                  subtitle="Dining area availability"
                  icon={Dining}
                  stats={tableStats}
                  iconColor="#10b981"
                />
              </Grid>
            </Grid>
          </Grid> */}

          {/* Hot Deals Section - Full Width */}
          {/* {activeOffers.length > 0 && (
            <Grid size={{ xs: 12 }}>
              <DashboardOffersSection offers={activeOffers} />
            </Grid>
          )} */}

          {/* Main Content - Orders, Tables, and Quick Actions */}
          <Grid size={{ xs: 12, md: 9 }} sx={{ display: "flex" }}>
            <Grid container spacing={3} sx={{ flex: 1 }}>
              {/* Orders Section */}
              <Grid
                size={{ xs: 12 }}
                sx={{ display: "flex", minHeight: "600px" }}
              >
                <DashboardOrdersSection
                  ordersData={ordersData}
                  loading={ordersLoading}
                  onViewAll={onViewAllOrders}
                  onSelectOrder={onSelectOrder}
                  onPageChange={handleOrdersPageChange}
                  viewMode={ordersViewMode}
                  onViewModeChange={setOrdersViewMode}
                  customerMobileSearch={customerMobileSearch}
                  setCustomerMobileSearch={setCustomerMobileSearch}
                  orderTypeFilter={orderTypeFilter}
                  setOrderTypeFilter={setOrderTypeFilter}
                />
              </Grid>

              {/* Tables Section */}
              {/* <Grid
                size={{ xs: 12, md: 3 }}
                sx={{ display: "flex", minHeight: "600px" }}
              >
                <DashboardTablesSection
                  tablesData={tablesData}
                  loading={tablesLoading}
                  stats={stats}
                  onViewAll={onViewAllTables}
                  onTableClick={onSelectTable}
                  onPageChange={handleTablesPageChange}
                />
              </Grid> */}
            </Grid>
          </Grid>

          {/* Quick Actions - Right Sidebar */}
          <Grid size={{ xs: 12, md: 3 }}>
            <DashboardQuickActionsSection
              onCreateDineIn={onCreateDineIn}
              onCreateTakeaway={onCreateTakeaway}
              isWaiter={isWaiter}
              dineInEnabled={orderTypeConfig?.Dinein !== false}
              takeawayEnabled={orderTypeConfig?.Takeaway !== false}
            />
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default POSDashboard;
